import type { OpportunityStatus, PolicySnapshot, ProductComparisonMatch } from '@fig/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AISummaryPanel } from '@/components/AISummaryPanel';
import { ComparisonTable } from '@/components/ComparisonTable';
import { DismissalModal } from '@/components/DismissalModal';
import { PreviousRecommendationSection } from '@/components/PreviousRecommendationSection';
import { ProductCardGrid } from '@/components/ProductCard';
import { ReviewModal, type ReviewApprovalData } from '@/components/ReviewModal';
import { ErrorState } from '@/components/common';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useAISummary } from '@/hooks/api/useAISummary';
import { useDismissRecommendationProduct } from '@/hooks/api/useDismissRecommendationProduct';
import { opportunityKeys } from '@/hooks/api/useOpportunities';
import { usePreviousRecommendations } from '@/hooks/api/usePreviousRecommendations';
import { useProductFacets } from '@/hooks/api/useProductFacets';
import apiClient from '@/lib/api-client';
import { ALL_FILTER_VALUE } from '@/lib/filter-constants';
import { normalizeProductTypeLabel } from '@/lib/product-types';
import { useTheme } from '@/providers/theme-provider';
import { useToast } from '@/providers/toast-provider';

import { useComparisonWithGenerate } from '../../hooks/api/useComparison';

import { ComparisonEmptyState } from './ComparisonEmptyState';
import { ComparisonHeader } from './ComparisonHeader';

export type ComparisonSortBy = 'match' | 'sp500';
type BrowseFailureAction = 'load' | 'search' | 'refresh' | 'sort';

function getSP500FromMatch(m: ProductComparisonMatch): number {
  for (const s of m.rateComparison?.strategyComparisons ?? []) {
    const upper = s.accountName.toUpperCase();
    const source = (s.accountSourceCode ?? '').toUpperCase();
    const isSP500 =
      upper.includes('S&P') ||
      upper.includes('SP500') ||
      source.includes('SP500') ||
      source.includes('S&P');
    const isPTP = upper.includes('POINT') || upper.includes('PTP') || upper.includes('ANNUAL');
    if (isSP500 && isPTP) return s.newRate;
  }
  return 0;
}

/**
 * Sort value for the Best Rate column. Mirrors what the column displays: the
 * S&P 500 PTP rate when present, otherwise the guaranteed yield to surrender
 * for Fixed Annuity Products (which have no S&P 500 strategy). Products with
 * neither sort last.
 */
function getBestRateSortValue(m: ProductComparisonMatch): number {
  const sp500 = getSP500FromMatch(m);
  if (sp500 > 0) return sp500;
  if (
    normalizeProductTypeLabel(m.annuityType) === 'Fixed Annuity Products' &&
    m.guaranteedYieldToSurrender != null
  ) {
    return m.guaranteedYieldToSurrender;
  }
  return 0;
}

function compareMatches(
  a: ProductComparisonMatch,
  b: ProductComparisonMatch,
  sortBy: ComparisonSortBy
): number {
  if (sortBy === 'sp500') {
    const bestRateDiff = getBestRateSortValue(b) - getBestRateSortValue(a);
    if (bestRateDiff !== 0) return bestRateDiff;
    return b.recommendationScore - a.recommendationScore;
  }

  const scoreDiff = b.recommendationScore - a.recommendationScore;
  if (scoreDiff !== 0) return scoreDiff;
  return getBestRateSortValue(b) - getBestRateSortValue(a);
}

function sortMatches(matches: ProductComparisonMatch[], sortBy: ComparisonSortBy) {
  return [...matches].sort((a, b) => compareMatches(a, b, sortBy));
}

function resolveMatchesByIds(
  ids: Iterable<string>,
  matches: ProductComparisonMatch[],
  sortBy: ComparisonSortBy
) {
  const idSet = new Set(ids);
  return sortMatches(
    matches.filter((match) => idSet.has(match.productId)),
    sortBy
  );
}

function fillSelectedDeck(
  selectedIds: Iterable<string>,
  selectedMatches: ProductComparisonMatch[],
  browseMatches: ProductComparisonMatch[],
  sortBy: ComparisonSortBy,
  maxMatches = 3
) {
  const catalog = new Map<string, ProductComparisonMatch>();

  for (const match of [...browseMatches, ...selectedMatches]) {
    catalog.set(match.productId, match);
  }

  const selectedIdSet = new Set(selectedIds);
  const lockedSelections = resolveMatchesByIds(selectedIdSet, Array.from(catalog.values()), sortBy);

  if (lockedSelections.length >= maxMatches) {
    return {
      checkedIds: new Set(lockedSelections.slice(0, maxMatches).map((match) => match.productId)),
      selectedMatches: lockedSelections.slice(0, maxMatches),
    };
  }

  const filler = sortMatches(
    Array.from(catalog.values()).filter((match) => !selectedIdSet.has(match.productId)),
    sortBy
  ).slice(0, maxMatches - lockedSelections.length);

  const nextSelectedMatches = [...lockedSelections, ...filler];

  return {
    checkedIds: new Set(nextSelectedMatches.map((match) => match.productId)),
    selectedMatches: nextSelectedMatches,
  };
}

interface ComparisonSectionProps {
  opportunityId: string;
  opportunityStatus: OpportunityStatus;
  currentPolicy: PolicySnapshot;
  /**
   * Called after a recommendation is forwarded to the advisor and the review
   * flow completes. Lets the parent decide where to go next (e.g. back to the
   * filtered list) rather than hard-navigating and dropping list state.
   */
  onForwardComplete: () => void;
}

export function ComparisonSection({
  opportunityId,
  opportunityStatus,
  currentPolicy,
  onForwardComplete,
}: ComparisonSectionProps) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const modalSurface = isDark ? 'hsl(210 30% 12%)' : 'hsl(0 0% 100%)';
  const modalBorderColor = isDark ? 'hsl(210 25% 24%)' : 'hsl(210 15% 82%)';
  const modalTextColor = isDark ? 'hsl(210 15% 93%)' : 'hsl(210 50% 10%)';
  const modalMutedTextColor = isDark ? 'hsl(210 12% 58%)' : 'hsl(210 12% 45%)';
  const [sortBy, setSortBy] = useState<ComparisonSortBy>('match');
  const [searchQuery, setSearchQuery] = useState('');
  // Column filters for the Type and Surrender columns. Applied server-side
  // against the full catalog (see buildBrowseRequest), not just displayed rows.
  const [typeFilter, setTypeFilter] = useState<string>(ALL_FILTER_VALUE);
  const [surrenderFilter, setSurrenderFilter] = useState<string>(ALL_FILTER_VALUE);

  // Layer 1: Selected top 3 — persistent, only changed by explicit user actions
  const [selectedMatches, setSelectedMatches] = useState<ProductComparisonMatch[]>([]);
  const [shownProductIds, setShownProductIds] = useState<string[]>([]);
  const [refreshingIndex, setRefreshingIndex] = useState<number | null>(null);
  const [dismissedProductIds, setDismissedProductIds] = useState<Set<string>>(new Set());
  const [pendingProductDismissal, setPendingProductDismissal] = useState<{
    match: ProductComparisonMatch;
    selectedIndex: number | null;
  } | null>(null);

  // Layer 2: Browse pool — always visible, filterable
  const [browseMatches, setBrowseMatches] = useState<ProductComparisonMatch[]>([]);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());

  // Review modal state
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rulesInfoOpen, setRulesInfoOpen] = useState(false);
  // The set of products the engine originally recommended.
  const initialRecommendedIdsRef = useRef<Set<string> | null>(null);

  const { comparison, isLoading, error, generate, isGenerating } =
    useComparisonWithGenerate(opportunityId);
  const { data: productFacets } = useProductFacets();
  const dismissRecommendationProduct = useDismissRecommendationProduct();
  const {
    data: previousRecommendations,
    isLoading: isPreviousRecommendationsLoading,
    error: previousRecommendationsError,
    refetch: refetchPreviousRecommendations,
  } = usePreviousRecommendations(opportunityId, {
    enabled: opportunityStatus === 'actioned',
  });

  const selectedProductIds = useMemo(
    () => selectedMatches.map((m) => m.productId),
    [selectedMatches]
  );
  const selectedProductKey = useMemo(() => selectedProductIds.join('|'), [selectedProductIds]);

  // Subscribe the review popup to the same AI Analysis query the opportunity-page panel uses.
  // Reading live from the shared React Query cache (rather than snapshotting once on open) keeps
  // the popup in sync whenever the analysis is regenerated — from here or from the panel.
  const { data: reviewSummary, isLoading: isReviewSummaryLoading } = useAISummary(
    opportunityId,
    reviewOpen,
    selectedProductIds
  );

  // The analysis no longer needs a forced regenerate when the advisor swaps products: the
  // summary cache is keyed on the product set as well as the opportunity, so a changed
  // selection is a cache miss and useAISummary generates a fresh one on its own (T20-3106).
  const handleOpenReview = useCallback(() => {
    if (selectedMatches.length === 0 || !selectedProductKey) return;

    setReviewOpen(true);
  }, [selectedMatches.length, selectedProductKey]);

  const handleReviewOpenChange = useCallback((open: boolean) => {
    setReviewOpen(open);
  }, []);

  const handleApproveForApplication = useCallback(
    async (data: ReviewApprovalData) => {
      try {
        await apiClient.post('/comparisons/forward', {
          opportunityId,
          ownerAge: currentPolicy.ownerAge,
          jurisdiction: currentPolicy.jurisdiction,
          relatedParties: currentPolicy.relatedParties ?? [],
          clientAge: currentPolicy.clientAge,
          clientState: currentPolicy.clientState,
          spouseAge: currentPolicy.spouseAge,
          currentCarrier: currentPolicy.carrierName,
          currentProduct: currentPolicy.productName,
          currentProductType: currentPolicy.productType,
          currentLineOfBusiness: currentPolicy.lineOfBusiness ?? null,
          currentPolicyStatus: currentPolicy.policyStatus ?? null,
          currentAccountValue: currentPolicy.accountValue,
          currentSurrenderValue: currentPolicy.cashSurrenderValue,
          currentPremium: currentPolicy.premium,
          currentSurrenderChargePct: currentPolicy.surrenderChargePercentage ?? null,
          currentCapRate: currentPolicy.currentCapRate,
          currentParticipationRate: currentPolicy.currentParticipationRate,
          currentGuaranteedRate: currentPolicy.guaranteedRate,
          currentPolicyAge: currentPolicy.policyAge ?? null,
          currentSurrenderDate: currentPolicy.surrenderDate,
          currentIncomeStatus: currentPolicy.incomeStatus,
          currentIncomeBenefitValue: currentPolicy.incomeBenefitValue,
          taxQualification: currentPolicy.taxQualification ?? null,
          recommendationSummary: data.editedSummary,
          originalRecommendationSummary: data.originalSummary,
          products: data.allocations.map((alloc, i) => {
            const match = selectedMatches.find((m) => m.productId === alloc.productId);
            const originalIdx = data.originalAllocations.findIndex(
              (o) => o.productId === alloc.productId
            );
            const originalAlloc = originalIdx >= 0 ? data.originalAllocations[originalIdx] : null;
            return {
              position: i + 1,
              originalPosition: originalIdx >= 0 ? originalIdx + 1 : null,
              productId: alloc.productId,
              carrierName: alloc.carrierName,
              productName: alloc.annuityName,
              productType: match?.annuityType ?? null,
              amBestRating: match?.bestRating ?? null,
              recommendationScore: match?.recommendationScore ?? null,
              surrenderPeriodYears: match?.surrenderPeriodYears ?? null,
              strategyAllocations: alloc.strategies.filter((s) => s.percentage > 0),
              originalStrategyAllocations: originalAlloc
                ? originalAlloc.strategies.filter((s) => s.percentage > 0)
                : [],
            };
          }),
        });
        await queryClient.invalidateQueries({ queryKey: opportunityKeys.all });
      } catch (err) {
        console.error('Failed to forward recommendation:', err);
        showToast({
          title: 'Unable to forward recommendation',
          description: 'Try again before leaving this opportunity.',
        });
        throw err;
      }
    },
    [opportunityId, currentPolicy, queryClient, selectedMatches, showToast]
  );

  // Search debounce
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sortRequestSeqRef = useRef(0);
  const selectedMatchesRef = useRef<ProductComparisonMatch[]>([]);
  const checkedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    selectedMatchesRef.current = selectedMatches;
  }, [selectedMatches]);

  useEffect(() => {
    checkedIdsRef.current = checkedIds;
  }, [checkedIds]);

  const buildBrowseRequest = useCallback(
    (
      query?: string,
      overrides?: {
        sortBy?: ComparisonSortBy;
        excludeProductIds?: string[];
        // Column-filter overrides. Passed explicitly by the filter change
        // handlers so the request reflects the new value before React state
        // has committed. Use the ALL sentinel to clear a filter.
        typeFilter?: string;
        surrenderFilter?: string;
      }
    ) => {
      const normalizedQuery = query?.trim() ?? '';
      const isSearching = normalizedQuery.length > 0;

      const effectiveTypeFilter = overrides?.typeFilter ?? typeFilter;
      const effectiveSurrenderFilter = overrides?.surrenderFilter ?? surrenderFilter;
      const annuityType =
        effectiveTypeFilter !== ALL_FILTER_VALUE ? effectiveTypeFilter : undefined;
      const surrenderPeriodYears =
        effectiveSurrenderFilter !== ALL_FILTER_VALUE
          ? Number(effectiveSurrenderFilter)
          : undefined;
      const isFiltering = annuityType !== undefined || surrenderPeriodYears !== undefined;

      // Searching or column-filtering both span the full sellable catalog rather
      // than only recommendation-eligible products, so a Type/Surrender filter
      // matches across the whole catalog (e.g. Fixed Annuity Products) instead
      // of only narrowing the already-displayed recommendations.
      const spanCatalog = isSearching || isFiltering;

      const excludedIds = new Set([
        ...dismissedProductIds,
        ...(overrides?.excludeProductIds ?? []),
      ]);

      return {
        forceRegenerate: true,
        maxMatches: spanCatalog ? 25 : 10,
        minUniqueCarriers: spanCatalog ? undefined : 7,
        searchQuery: isSearching ? normalizedQuery : undefined,
        searchAllProducts: spanCatalog ? true : undefined,
        annuityType,
        surrenderPeriodYears,
        sortBy: overrides?.sortBy ?? sortBy,
        excludeProductIds: excludedIds.size > 0 ? [...excludedIds] : undefined,
      };
    },
    [dismissedProductIds, sortBy, surrenderFilter, typeFilter]
  );

  const handleBrowseFailure = useCallback(
    (error: unknown, action: BrowseFailureAction) => {
      const title =
        action === 'search' ? 'Unable to search products' : 'Unable to load replacement products';
      const description =
        action === 'refresh'
          ? 'Try regenerating the product list again.'
          : 'The current recommendations are still available.';

      console.error(`Failed to ${action} replacement products:`, error);
      showToast({ title, description });
    },
    [showToast]
  );

  // When initial comparison loads, set selected matches and fetch browse pool
  const initializedRef = useRef(false);
  useEffect(() => {
    if (comparison?.matches && comparison.matches.length > 0 && !initializedRef.current) {
      initializedRef.current = true;
      const top3 = comparison.matches.slice(0, 3);
      initialRecommendedIdsRef.current = new Set(top3.map((m) => m.productId));
      setSelectedMatches(top3);
      setShownProductIds(comparison.matches.map((m) => m.productId));
      setCheckedIds(new Set(top3.map((m) => m.productId)));
      // Fetch the full browse pool
      void generate(buildBrowseRequest(undefined, { sortBy }))
        .then((res) => {
          if (res.comparison?.matches) {
            setBrowseMatches(res.comparison.matches);
          }
        })
        .catch((error) => handleBrowseFailure(error, 'load'));
    }
  }, [buildBrowseRequest, comparison, generate, handleBrowseFailure, sortBy]);

  // Fetch browse pool (debounced when searching)
  const fetchBrowsePool = useCallback(
    (
      query?: string,
      overrides?: { sortBy?: ComparisonSortBy; typeFilter?: string; surrenderFilter?: string }
    ) => {
      void generate(buildBrowseRequest(query, overrides))
        .then((res) => {
          if (res.comparison?.matches) {
            setBrowseMatches(res.comparison.matches);
          }
        })
        .catch((error) => handleBrowseFailure(error, query?.trim() ? 'search' : 'load'));
    },
    [buildBrowseRequest, generate, handleBrowseFailure]
  );

  // Handle search input — debounce 400ms, only affects browse pool
  const handleSearchChange = useCallback(
    (query: string) => {
      setSearchQuery(query);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        fetchBrowsePool(query);
      }, 400);
    },
    [fetchBrowsePool]
  );

  // Column filters (Type / Surrender) refetch the browse pool immediately so
  // they narrow the full catalog server-side rather than the displayed rows.
  const handleTypeFilterChange = useCallback(
    (value: string) => {
      setTypeFilter(value);
      fetchBrowsePool(searchQuery, { typeFilter: value });
    },
    [fetchBrowsePool, searchQuery]
  );

  const handleSurrenderFilterChange = useCallback(
    (value: string) => {
      setSurrenderFilter(value);
      fetchBrowsePool(searchQuery, { surrenderFilter: value });
    },
    [fetchBrowsePool, searchQuery]
  );

  // Regenerate browse pool — exclude current unchecked items to show fresh results
  const handleRefreshAll = useCallback(() => {
    const exclusions = browseMatches
      .filter((m) => !checkedIds.has(m.productId))
      .map((m) => m.productId);

    void generate(
      buildBrowseRequest(searchQuery, {
        excludeProductIds: exclusions.length > 0 ? exclusions : undefined,
      })
    )
      .then((res) => {
        if (res.comparison?.matches) {
          setBrowseMatches(res.comparison.matches);
        }
      })
      .catch((error) => handleBrowseFailure(error, searchQuery.trim() ? 'search' : 'refresh'));
  }, [browseMatches, buildBrowseRequest, checkedIds, generate, handleBrowseFailure, searchQuery]);

  const fetchReplacementForSlot = useCallback(
    async (
      index: number,
      currentMatch: ProductComparisonMatch,
      extraExcludeProductIds: string[] = []
    ): Promise<ProductComparisonMatch | null> => {
      setRefreshingIndex(index);
      const otherCarriers = selectedMatches.filter((_, i) => i !== index).map((m) => m.carrierName);
      const allShown = [
        ...new Set([
          ...shownProductIds,
          ...dismissedProductIds,
          currentMatch.productId,
          ...extraExcludeProductIds,
        ]),
      ];
      setShownProductIds(allShown);

      try {
        const res = await generate({
          forceRegenerate: true,
          maxMatches: 1,
          excludeProductIds: allShown,
          excludeCarriers: otherCarriers,
          sortBy,
        });

        const newMatch = res.comparison?.matches?.[0] ?? null;
        if (newMatch) {
          setSelectedMatches((prev) => {
            const updated = [...prev];
            updated[index] = newMatch;
            return updated;
          });
          setShownProductIds((prev) => [...new Set([...prev, newMatch.productId])]);
          // Keep the browse-table checkboxes in sync with the selected deck: the slot's
          // previous product is no longer selected, and the replacement now is. Without this
          // the old product stays checked in the bottom list and the new one shows unchecked.
          setCheckedIds((prev) => {
            const next = new Set(prev);
            next.delete(currentMatch.productId);
            next.add(newMatch.productId);
            return next;
          });
        }

        return newMatch;
      } finally {
        setRefreshingIndex(null);
      }
    },
    [dismissedProductIds, generate, selectedMatches, shownProductIds, sortBy]
  );

  // Refresh a single card
  const handleRefreshCard = useCallback(
    (index: number, currentMatch: ProductComparisonMatch) => {
      void fetchReplacementForSlot(index, currentMatch).catch((error) => {
        console.error('Failed to refresh recommendation:', error);
        showToast({
          title: 'Unable to refresh recommendation',
          description: 'Try again or use the product table to select another option.',
        });
      });
    },
    [fetchReplacementForSlot, showToast]
  );

  // Sort change — re-sorts selected 3 client-side, refetches browse pool
  const handleSortChange = useCallback(
    (newSortBy: ComparisonSortBy) => {
      const requestSeq = sortRequestSeqRef.current + 1;
      sortRequestSeqRef.current = requestSeq;

      setSortBy(newSortBy);
      setSelectedMatches((prev) => {
        const nextSelectedMatches = sortMatches(prev, newSortBy);
        selectedMatchesRef.current = nextSelectedMatches;
        return nextSelectedMatches;
      });

      void generate(buildBrowseRequest(searchQuery, { sortBy: newSortBy }))
        .then((res) => {
          if (requestSeq !== sortRequestSeqRef.current) return;
          if (!res.comparison?.matches) return;

          const nextBrowseMatches = res.comparison.matches;
          setBrowseMatches(nextBrowseMatches);

          const currentCheckedIds = checkedIdsRef.current;
          const currentSelectedMatches = selectedMatchesRef.current;

          if (currentCheckedIds.size < 3) {
            const nextDeck = fillSelectedDeck(
              currentCheckedIds,
              currentSelectedMatches,
              nextBrowseMatches,
              newSortBy
            );
            checkedIdsRef.current = nextDeck.checkedIds;
            selectedMatchesRef.current = nextDeck.selectedMatches;
            setCheckedIds(() => nextDeck.checkedIds);
            setSelectedMatches(() => nextDeck.selectedMatches);
            return;
          }

          setSelectedMatches((currentMatches) => {
            const nextSelectedMatches = sortMatches(currentMatches, newSortBy);
            selectedMatchesRef.current = nextSelectedMatches;
            return nextSelectedMatches;
          });
        })
        .catch((error) => {
          if (requestSeq !== sortRequestSeqRef.current) return;
          handleBrowseFailure(error, 'sort');
        });
    },
    [buildBrowseRequest, generate, handleBrowseFailure, searchQuery]
  );

  // Toggle checkbox — auto-swap: checking a 4th auto-unchecks the weakest
  const handleToggleCheck = useCallback(
    (match: ProductComparisonMatch) => {
      // Build full item list for carrier/score lookups
      const selIds = new Set(selectedMatches.map((m) => m.productId));
      const rest = browseMatches.filter((m) => !selIds.has(m.productId));
      const allItems = [...selectedMatches, ...rest];

      const isCurrentlyChecked = checkedIds.has(match.productId);

      if (isCurrentlyChecked) {
        // Uncheck — remove from selected
        const next = new Set(checkedIds);
        next.delete(match.productId);
        setCheckedIds(next);
        const newSelected = resolveMatchesByIds(next, allItems, sortBy).slice(0, 3);
        setSelectedMatches(newSelected);
        return;
      }

      // Checking a new product — no carrier restriction for manual table selection
      // (unique carriers are only enforced on initial generation and per-card refresh)
      if (checkedIds.size < 3) {
        // Under 3 — just add
        const next = new Set(checkedIds);
        next.add(match.productId);
        setCheckedIds(next);
        const newSelected = resolveMatchesByIds(next, allItems, sortBy).slice(0, 3);
        setSelectedMatches(newSelected);
      } else {
        // Already 3 checked — auto-swap: remove the weakest product in the active sort mode
        const currentChecked = resolveMatchesByIds(checkedIds, allItems, sortBy);
        const weakest = currentChecked[currentChecked.length - 1];
        if (weakest) {
          const next = new Set(checkedIds);
          next.delete(weakest.productId);
          next.add(match.productId);
          setCheckedIds(next);
          const newSelected = resolveMatchesByIds(next, allItems, sortBy).slice(0, 3);
          setSelectedMatches(newSelected);
        }
      }
    },
    [browseMatches, checkedIds, selectedMatches, sortBy]
  );

  const handleDismissTableProduct = useCallback(
    (match: ProductComparisonMatch) => {
      const selectedIndex = selectedMatches.findIndex((m) => m.productId === match.productId);
      setPendingProductDismissal({
        match,
        selectedIndex: selectedIndex >= 0 ? selectedIndex : null,
      });
    },
    [selectedMatches]
  );

  const handleDismissProductConfirm = useCallback(
    async ({ comment }: { comment: string }) => {
      if (!pendingProductDismissal) return;

      const { match } = pendingProductDismissal;
      const currentSelectedIndex = selectedMatches.findIndex(
        (m) => m.productId === match.productId
      );
      const selectedIndex =
        currentSelectedIndex >= 0
          ? currentSelectedIndex
          : (pendingProductDismissal.selectedIndex ?? -1);

      try {
        await dismissRecommendationProduct.mutateAsync({
          opportunityId,
          productId: match.productId,
          carrierName: match.carrierName,
          productName: match.annuityName,
          productType: match.annuityType ?? null,
          recommendationScore: match.recommendationScore ?? null,
          comment,
        });

        setDismissedProductIds((prev) => new Set([...prev, match.productId]));
        setBrowseMatches((prev) => prev.filter((m) => m.productId !== match.productId));

        if (selectedIndex >= 0) {
          let replacement: ProductComparisonMatch | null = null;
          try {
            replacement = await fetchReplacementForSlot(selectedIndex, match, [match.productId]);
          } catch (error) {
            console.error('Failed to recycle dismissed recommendation:', error);
          }

          if (replacement) {
            // fetchReplacementForSlot already swapped the dismissed product for the
            // replacement in checkedIds, keeping the browse-table selection in sync.
            showToast({
              title: 'Product dismissed',
              description: 'A replacement recommendation was loaded.',
            });
          } else {
            setSelectedMatches((prev) => prev.filter((m) => m.productId !== match.productId));
            setCheckedIds((prev) => {
              const next = new Set(prev);
              next.delete(match.productId);
              return next;
            });
            showToast({
              title: 'Product dismissed',
              description: 'No replacement was available for this slot.',
            });
          }
        } else {
          setCheckedIds((prev) => {
            const next = new Set(prev);
            next.delete(match.productId);
            return next;
          });
          showToast({
            title: 'Product dismissed',
            description: 'It will not be recommended again for this opportunity.',
          });
        }

        setPendingProductDismissal(null);
      } catch (error) {
        console.error('Failed to dismiss recommendation product:', error);
        showToast({
          title: 'Unable to dismiss product',
          description: 'Try again or refresh the page.',
        });
      }
    },
    [
      dismissRecommendationProduct,
      fetchReplacementForSlot,
      opportunityId,
      pendingProductDismissal,
      selectedMatches,
      showToast,
    ]
  );

  // Browse table items: stable order from API, with selected items included if not already present
  const browseTableItems = useMemo(() => {
    const browseIds = new Set(browseMatches.map((m) => m.productId));
    // Add any selected items missing from browse results (e.g., from per-card refresh)
    const missing = selectedMatches.filter((m) => !browseIds.has(m.productId));
    return [...browseMatches, ...missing].filter(
      (match) => !dismissedProductIds.has(match.productId)
    );
  }, [browseMatches, dismissedProductIds, selectedMatches]);

  // Catalog-wide filter options (Type / Surrender). Sourced from the whole
  // active catalog rather than the currently displayed products, so filtering
  // spans every product — not just the browse pool on screen.
  const typeOptions = useMemo(() => {
    const types = new Set<string>();
    for (const rawType of productFacets?.annuityTypes ?? []) {
      types.add(normalizeProductTypeLabel(rawType));
    }
    return [...types].sort((a, b) => a.localeCompare(b));
  }, [productFacets]);

  const surrenderOptions = productFacets?.surrenderPeriods ?? [];

  // Type/Surrender filtering happens server-side (see buildBrowseRequest); the
  // browse pool returned already reflects them. Only the search box narrows
  // further on the client, for snappy typing feedback.
  const filteredBrowseTableItems = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toUpperCase();
    if (!normalizedQuery) return browseTableItems;

    return browseTableItems.filter((match) => {
      const carrier = match.carrierName.toUpperCase();
      const product = match.annuityName.toUpperCase();
      return carrier.includes(normalizedQuery) || product.includes(normalizedQuery);
    });
  }, [browseTableItems, searchQuery]);

  const hasData =
    browseMatches.length > 0 ||
    selectedMatches.length > 0 ||
    (comparison?.matches?.length ?? 0) > 0;
  // With no recommendations and nothing picked yet, the cards give way to the
  // empty state, but the search, filters and table stay mounted below it so a
  // reviewer can still choose products from the full catalog (T20-3441).
  const showCards = selectedMatches.length > 0 || (comparison?.matches?.length ?? 0) > 0;

  return (
    <div className="space-y-4">
      {isLoading || (isGenerating && !hasData && comparison == null) ? (
        <div className="space-y-4 py-8">
          <Skeleton className="mx-auto h-4 w-48" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Skeleton className="h-48 rounded-lg" />
            <Skeleton className="h-48 rounded-lg" />
            <Skeleton className="h-48 rounded-lg" />
          </div>
        </div>
      ) : error ? (
        <ErrorState
          message="Failed to load comparison. Please try again."
          onRetry={handleRefreshAll}
        />
      ) : (
        <div className="space-y-4">
          {opportunityStatus === 'actioned' && (
            <PreviousRecommendationSection
              recommendation={previousRecommendations}
              isLoading={isPreviousRecommendationsLoading}
              error={previousRecommendationsError}
              onRetry={() => void refetchPreviousRecommendations()}
            />
          )}

          {/* 1. "Product Comparison" title + selected top 3 cards */}
          {showCards ? (
            <>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-1.5">
                  <h2 className="text-lg font-semibold">Product Comparison</h2>
                  <button
                    type="button"
                    className="inline-flex rounded-full text-muted-foreground/60 transition-colors hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="How product recommendations are determined"
                    onClick={() => setRulesInfoOpen(true)}
                  >
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <ProductCardGrid
                matches={selectedMatches}
                onRefreshCard={handleRefreshCard}
                refreshingIndex={refreshingIndex}
              />

              {/* 2. Forward for Review */}
              <div className="flex items-center justify-between rounded-lg border border-border bg-gradient-card px-5 py-3 shadow-elevation-2">
                <span className="text-sm text-muted-foreground">
                  Forward the selected three replacement options to the advisor
                </span>
                <button
                  className="rounded-md px-5 py-2 text-sm font-medium text-white transition-colors hover:opacity-90 shrink-0 ml-4"
                  style={{ backgroundColor: 'hsl(211 47% 20%)' }}
                  disabled={selectedMatches.length === 0}
                  onClick={() => void handleOpenReview()}
                >
                  Forward for Review
                </button>
              </div>
            </>
          ) : (
            <ComparisonEmptyState type="no-matches" />
          )}

          <ReviewModal
            open={reviewOpen}
            onOpenChange={handleReviewOpenChange}
            matches={selectedMatches}
            aiSummary={reviewSummary ?? null}
            isSummaryLoading={isReviewSummaryLoading}
            onApprove={handleApproveForApplication}
            onComplete={onForwardComplete}
          />

          <Dialog open={rulesInfoOpen} onOpenChange={setRulesInfoOpen}>
            <DialogContent
              className="max-w-2xl max-h-[85vh] overflow-y-auto"
              style={{
                backgroundColor: modalSurface,
                borderColor: modalBorderColor,
                color: modalTextColor,
              }}
            >
              <DialogHeader>
                <DialogTitle style={{ color: modalTextColor }}>
                  How product recommendations are determined
                </DialogTitle>
                <DialogDescription style={{ color: modalMutedTextColor }}>
                  Every candidate product is checked against these carrier and product rules before
                  it can appear in this comparison. Carrier exclusions and boosts differ by
                  strategy.
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg border p-3" style={{ borderColor: modalBorderColor }}>
                  <p className="text-sm font-medium" style={{ color: modalTextColor }}>
                    Income strategy
                  </p>
                  <div className="mt-2 space-y-2 text-sm">
                    <div>
                      <p className="text-xs font-medium" style={{ color: modalMutedTextColor }}>
                        Excluded carriers
                      </p>
                      <p style={{ color: modalMutedTextColor }}>
                        National Western Life, Prudential, Symetra, Nassau, Protective Life,
                        National Life Group, American National, Oxford
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium" style={{ color: modalMutedTextColor }}>
                        Excluded products
                      </p>
                      <p style={{ color: modalMutedTextColor }}>Assured Edge Income Achiever</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium" style={{ color: modalMutedTextColor }}>
                        Boosted carriers (+5 match score)
                      </p>
                      <p style={{ color: modalMutedTextColor }}>
                        EquiTrust, American Equity, North American, Talcott, F&amp;G, Corebridge
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border p-3" style={{ borderColor: modalBorderColor }}>
                  <p className="text-sm font-medium" style={{ color: modalTextColor }}>
                    Accumulation strategy
                  </p>
                  <div className="mt-2 space-y-2 text-sm">
                    <div>
                      <p className="text-xs font-medium" style={{ color: modalMutedTextColor }}>
                        Excluded carriers
                      </p>
                      <p style={{ color: modalMutedTextColor }}>
                        Nassau, National Western Life, Symetra, Prudential, F&amp;G, Protective
                        Life, National Life Group, Talcott, Nationwide, Corebridge, Global Atlantic,
                        Oxford, Ameritas
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium" style={{ color: modalMutedTextColor }}>
                        Excluded products
                      </p>
                      <p style={{ color: modalMutedTextColor }}>None</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium" style={{ color: modalMutedTextColor }}>
                        Boosted carriers (+5 match score)
                      </p>
                      <p style={{ color: modalMutedTextColor }}>
                        Standard, Ibexis, Oceanview, MassMutual, Lincoln Financial
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <ul className="space-y-3 text-sm">
                <li>
                  <span className="font-medium" style={{ color: modalTextColor }}>
                    Minimum financial strength
                  </span>
                  <p style={{ color: modalMutedTextColor }}>
                    Carriers rated below B++ (AM Best) are excluded outright.
                  </p>
                </li>
                <li>
                  <span className="font-medium" style={{ color: modalTextColor }}>
                    State availability
                  </span>
                  <p style={{ color: modalMutedTextColor }}>
                    The product must be licensed and sellable in the client's state.
                  </p>
                </li>
                <li>
                  <span className="font-medium" style={{ color: modalTextColor }}>
                    Issue age limits
                  </span>
                  <p style={{ color: modalMutedTextColor }}>
                    The product must allow issuance at the client's current age.
                  </p>
                </li>
                <li>
                  <span className="font-medium" style={{ color: modalTextColor }}>
                    Minimum premium
                  </span>
                  <p style={{ color: modalMutedTextColor }}>
                    The account value must meet the product's minimum premium requirement.
                  </p>
                </li>
                <li>
                  <span className="font-medium" style={{ color: modalTextColor }}>
                    Premium band and bonus age
                  </span>
                  <p style={{ color: modalMutedTextColor }}>
                    Rates and the premium bonus follow the band and issue-age tier published for the
                    amount that transfers (cash surrender value) and the annuitant's age. A premium
                    of $100,000 selects a band whose minimum is $100,000. Issue age 76 selects a
                    bonus whose from-age is 76. When the premium, age, or tax qualification is
                    unknown, the lower band and lower bonus are shown. Products that publish a
                    single rate and bonus keep those figures.
                  </p>
                </li>
                <li>
                  <span className="font-medium" style={{ color: modalTextColor }}>
                    Platform availability
                  </span>
                  <p style={{ color: modalMutedTextColor }}>
                    The product must be enabled for e-application submission.
                  </p>
                </li>
                <li>
                  <span className="font-medium" style={{ color: modalTextColor }}>
                    Income rider match
                  </span>
                  <p style={{ color: modalMutedTextColor }}>
                    If the current policy has an active income rider, replacement candidates must
                    offer one too.
                  </p>
                </li>
              </ul>
            </DialogContent>
          </Dialog>

          {/* 3. AI Summary */}
          {showCards && (
            <AISummaryPanel
              opportunityId={opportunityId}
              hasComparison
              selectedProductIds={selectedProductIds}
            />
          )}

          {/* 4. Filters above table */}
          <ComparisonHeader
            hasComparison
            onRegenerate={handleRefreshAll}
            isRegenerating={isGenerating && refreshingIndex === null}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            sortBy={sortBy}
            onSortChange={handleSortChange}
            typeFilter={typeFilter}
            typeOptions={typeOptions}
            onTypeFilterChange={handleTypeFilterChange}
            surrenderFilter={surrenderFilter}
            surrenderOptions={surrenderOptions}
            onSurrenderFilterChange={handleSurrenderFilterChange}
          />

          {/* 5. Always-open product table with checkboxes — auto-swap on check */}
          <div>
            <ComparisonTable
              opportunityId={opportunityId}
              currentPolicy={currentPolicy}
              matches={filteredBrowseTableItems}
              selectable
              checkedIds={checkedIds}
              onToggleCheck={handleToggleCheck}
              onDismissProduct={handleDismissTableProduct}
            />
          </div>

          <DismissalModal
            open={pendingProductDismissal !== null}
            onOpenChange={(open) => {
              if (!open) setPendingProductDismissal(null);
            }}
            title="Dismiss Product Recommendation"
            description="This product will not be recommended again for this opportunity, but it can still appear for other opportunities."
            subjectLabel={
              pendingProductDismissal
                ? `${pendingProductDismissal.match.carrierName} ${pendingProductDismissal.match.annuityName}`
                : undefined
            }
            commentLabel="Why is this product a poor fit for this opportunity?"
            commentPlaceholder="Add context for why this product should not be recommended for this opportunity..."
            confirmLabel="Dismiss Product"
            isSubmitting={dismissRecommendationProduct.isPending || refreshingIndex !== null}
            onConfirm={handleDismissProductConfirm}
          />
        </div>
      )}
    </div>
  );
}
